// OurFamilyWizard "Message Report" PDF → canonical corpus.csv.
//
// Direct port of evals/adapters/ofw.py — keep the parsing in lockstep
// so a PDF run through the browser yields a byte-identical canonical
// CSV to the desktop / Python adapter (D0.3 spike validated 2,778/2,778
// rows on demo-bravo).
//
// See the source's module docstring for the OFW format reference + the
// 2026-04-21 intake-session design decisions (monitor on To:, subject
// in its own column, first-viewed in source metadata, timezone-aware
// timestamp conversion), and for the SECOND report layout OFW ships since
// 2026-10 — the conversation-grouped "Messages Report" (`Message n of k
// Conversation c of C` headers, inline capital SENT/FROM, TO, SUBJECT
// lines, `Conversation N` separators, a bare `Page N of M` footer, a cover
// whose contact column merges onto the label lines under pdfplumber but
// not under pdf.js). Same canonical output; the conversation template
// additionally carries conversation_index / index_in_conversation per row.
// Since 2026-10-28 a THIRD layout: the same redesigned report without
// grouping ("Individual messages": `Message N of M` headers over the inline
// capital lines, bare footer, no quoted history, no conversation fields).
// Told from the old per-message report by the line after the first header.
import { DateTime, IANAZone } from "luxon";

import { sanitizeActor, sanitizeBody } from "./_normalize";
import { extractPdfTextEol, type PdfExtractProgress } from "./_pdf";

// 0.3.0 (2026-10-27): the conversation-grouped template parses alongside the
// per-message report; per-message output is unchanged. 0.4.0 (2026-10-28):
// the redesigned report's ungrouped layout parses as a third template.
// Lockstep with ofw.py.
export const PARSER_VERSION = "0.4.0";
export const ADAPTER_NAME = "ofw";

// The three OFW report layouts this adapter reads. Lockstep with ofw.py.
export type OfwTemplate = "per-message" | "conversation-grouped" | "individual-messages";
export const TEMPLATE_PER_MESSAGE: OfwTemplate = "per-message";
export const TEMPLATE_CONVERSATION: OfwTemplate = "conversation-grouped";
export const TEMPLATE_INDIVIDUAL: OfwTemplate = "individual-messages";
// The two redesigned layouts share the inline SENT/TO/SUBJECT grammar, the
// bare `Page N of M` footer and a body with no quoted history.
const INLINE_TEMPLATES: ReadonlySet<OfwTemplate> = new Set([
  TEMPLATE_CONVERSATION,
  TEMPLATE_INDIVIDUAL,
]);

// Cover-less exports (page 1 starts at `Message 1 of N`, no cover/summary
// page) carry no Timezone: header, so we cannot read the corpus zone. Fall
// back to this zone, but flag the assumption loudly (stats.timezone_assumed +
// case meta + result.warnings) so it never reads as authoritative. Lockstep
// with evals/adapters/ofw.py _DEFAULT_ASSUMED_TIMEZONE.
const DEFAULT_ASSUMED_TIMEZONE = "America/New_York";

const FOOTER_RE = /^\|?\s*Message Report Page \d+ of \d+\s*$/;
// The redesigned templates' footer is a bare `Page N of M`: stripped only
// from the LAST line of a page and only for that template (ofw.py
// _BARE_FOOTER_RE), so a body line shaped like a page marker is never
// touched elsewhere.
const BARE_FOOTER_RE = /^Page \d+ of \d+\s*$/;
// Each template splits on ITS OWN header shape only (a body line shaped
// like the other template's header is text). Per-message: exactly `Message
// N of M`. Conversation: `Message n of k Conversation c of C`; pdf.js may
// break that wide row after `k` (its column-gap rule), so the two halves may
// also sit on consecutive lines — matched in place, the text is never
// rewritten. Lockstep with ofw.py _MSG_HEADER_PM_RE / _MSG_HEADER_CONV_RE.
const MSG_HEADER_PM_RE_GLOBAL = /^Message (\d+) of (\d+)[ \t]*$/gm;
const MSG_HEADER_CONV_RE_GLOBAL =
  /^Message (\d+) of (\d+)(?:[ \t]+|[ \t]*\n[ \t]*)Conversation (\d+) of (\d+)[ \t]*$/gm;
// Either shape; used only to pick the template from the FIRST header.
const MSG_HEADER_ANY_RE =
  /^Message (\d+) of (\d+)(?:(?:[ \t]+|[ \t]*\n[ \t]*)Conversation (\d+) of (\d+))?[ \t]*$/m;
// `Conversation N` separator line before each conversation's first message.
const CONVERSATION_SEP_RE = /^Conversation (\d+)\s*$/;
// Redesigned-template header trio (grouped and individual; inline,
// capitals, no colons). pdf.js
// may also break the `SENT … FROM …` row after the datetime; then the two
// halves arrive on consecutive lines (SENT_ONLY_RE + FROM_ONLY_RE).
const SENT_FROM_RE = /^SENT\s+(.+?)\s+FROM\s+(.+?)\s*$/;
const SENT_ONLY_RE = /^SENT\s+(.+?)\s*$/;
const FROM_ONLY_RE = /^FROM\s+(.+?)\s*$/;
const TO_CAPS_RE = /^TO\s+(.+?)\s*$/;
const SUBJECT_CAPS_RE = /^SUBJECT(?:\s+(.*?))?\s*$/;
const FIRST_VIEWED_RE = /\s*\(First Viewed:\s*([^)]+)\)\s*$/;
const ATT_ITEM_RE =
  /([^,()]+?)\s*\((\d+(?:\.\d+)?\s*(?:KB|MB|GB|bytes|B))\)/gi;
// The `at` separator is present in cover-bearing exports
// ("03/01/2024 at 09:00 AM") but absent in some cover-less exports
// ("05/10/2021 3:04 PM"); accept either. Lockstep with ofw.py _DATETIME_RE.
const DATETIME_RE =
  /(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(?:at\s+)?(\d{1,2}):(\d{2})\s*(AM|PM)/i;

export class MalformedOFWError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "MalformedOFWError";
  }
}

// --- Canonical row + metadata shapes ---

export interface CanonicalRow {
  timestamp: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  attachments: string;
}

export interface SourceMetadata {
  source: string;
  message_number: number;
  to_raw: string[];
  first_viewed_by: Record<string, string>;
  subject: string;
  attachments_structured: { filename: string; size: string }[];
  // Conversation template only: the thread this message belongs to and its
  // position within it (1-based, as printed). Absent on the per-message
  // template. Lockstep with ofw.py SourceMetadata.
  conversation_index?: number;
  index_in_conversation?: number;
}

export interface CoverInfo {
  timezone_name: string;
  party_a: string;
  party_b: string;
  total_messages: number;
  children: string[];
  // `Contains: N selected messages across C conversations` — null when the
  // cover carries no conversation count (per-message template, or
  // synthesized cover).
  conversation_count: number | null;
}

export interface ParsedMessage {
  message_number: number;
  sent_raw: string;
  from_name: string;
  to_lines: string[];
  subject: string;
  body: string;
  attachments_raw: string | null;
  conversation_index?: number; // conversation template only
  index_in_conversation?: number; // conversation template only
}

// One message's raw text plus its position in the report. `message_number`
// is OFW's global `Message N of M` on the per-message template and the
// running document order (1-based) on the conversation template, where the
// printed `n of k` is per conversation. Lockstep with ofw.py Stanza.
export interface Stanza {
  message_number: number;
  text: string;
  conversation_index?: number;
  index_in_conversation?: number;
  conversation_size?: number;
}

export interface IngestStats {
  party_a: string;
  party_b: string;
  out_of_set_senders: string[];
  message_count: number;
  accepted_count: number;
  rejected_count: number;
  non_printables_stripped: number;
  rejection_rate_by_party: Record<string, number>;
  date_range: { start: string | null; end: string | null };
  timezone: string;
  children: string[];
  // Which OFW report layout was read, and for the conversation template how
  // many conversation threads the export declares (null otherwise).
  template: OfwTemplate;
  conversation_count: number | null;
  // Header-shaped lines that broke OFW's numbering sequence and were kept
  // as body text (see HeaderSequence). 0 on every real export seen; each
  // one is also a warning. Attached by ingestOfwFromPages.
  header_lines_in_bodies?: number;
  // Page-break reprint lines collapsed (dedupPageBoundaries), with the
  // removed text (each truncated) as the audit trail. Mirrors the Python
  // ingest_stats.json fields. Attached by ingestOfwFromPages.
  page_boundary_dups_collapsed?: number;
  page_boundary_collapsed_lines?: string[];
  // Stanzas skipped as malformed under the 10% tolerance (each is also a
  // warning). Lockstep with the Python "malformed skipped" count.
  malformed_skipped?: number;
  // True only for cover-less exports, where the corpus timezone couldn't be
  // read from a cover page and was assumed (see DEFAULT_ASSUMED_TIMEZONE).
  // The UI / user should verify and correct the zone. Always present;
  // false on cover-bearing exports.
  timezone_assumed: boolean;
  // PDF page count from the source OFW export. The cost-screen tier
  // context line prefers this number over the token-derived
  // pages_estimate when present — verifiable against the user's PDF.
  // Optional because `buildRowsAndMetadata` doesn't see the page list;
  // `ingestOfwPdf` attaches it after the rows pass.
  page_count?: number;
}

export interface IngestResult {
  rows: CanonicalRow[];
  source_metas: SourceMetadata[];
  stats: IngestStats;
  cover: CoverInfo;
  /** Canonical CSV text (UTF-8, RFC 4180 quoting). Ready to upload. */
  csv: string;
  /** Auto-built case-level meta.json (id is the user-supplied slug). */
  case_meta: CaseMeta;
  /**
   * Non-fatal ingest warnings surfaced to the caller (e.g. the loud
   * cover-less timezone-assumed notice). Empty on a clean cover-bearing
   * ingest. Mirrors the WARN lines the Python adapter writes to ingest.log.
   */
  warnings: string[];
}

export interface CaseMeta {
  id: string;
  privacy_class: string;
  prompt_vars: {
    party_a: string;
    party_b: string;
    format: string;
    time_period: string;
    context: string;
  };
  known_patterns: string[];
  notes: string;
  // Cover-less exports assume a timezone; surface that loudly so the user
  // can verify/correct the source_timezone / predicted_timezone /
  // user_timezone fields. Absent (not just false) on cover-bearing exports
  // so existing meta files stay byte-identical. Mirrors ofw.py.
  timezone_assumed?: boolean;
  // Source-format metadata surfaced from the adapter. PDF sources
  // carry page_count; non-PDF adapters (Civil Communicator txt,
  // generic CSV) emit `{}` and downstream falls back to the
  // token-derived pages_estimate on the cost screen. OFW PDFs also
  // record which report layout was read (and its conversation count).
  ingestion: {
    page_count?: number;
    template?: OfwTemplate;
    conversation_count?: number | null;
  };
}

// --- Stage 1-2: extract + concatenate pages ---

function headerRe(template: OfwTemplate): RegExp {
  return template === TEMPLATE_CONVERSATION
    ? MSG_HEADER_CONV_RE_GLOBAL
    : MSG_HEADER_PM_RE_GLOBAL;
}

// Which OFW layout this is, from the FIRST header and the line after it.
// `Message n of k Conversation c of C` is the conversation-grouped template.
// A plain `Message N of M` is either the old per-message report or the
// redesigned ungrouped layout; the first non-empty line after the header
// tells them apart (`Sent:` there, `SENT …` here; pdf.js may break that row
// after the datetime, it still starts with SENT). Only the first header
// decides: it precedes every message body, so nothing a party typed can
// reach it, whereas a later header-shaped line can be user text (one typed
// line must not flip the grammar for the whole file). Decided before footers
// are stripped. Lockstep with ofw.py _detect_template.
export function detectTemplate(pages: string[]): OfwTemplate {
  for (let i = 0; i < pages.length; i++) {
    const m = pages[i].match(MSG_HEADER_ANY_RE);
    if (m) {
      if (m[3]) return TEMPLATE_CONVERSATION;
      const first = firstLineAfter(pages, i, (m.index ?? 0) + m[0].length);
      return SENT_ONLY_RE.test(first) ? TEMPLATE_INDIVIDUAL : TEMPLATE_PER_MESSAGE;
    }
  }
  return TEMPLATE_PER_MESSAGE;
}

// First non-empty, non-footer line after `pos` on page `pageIndex`,
// continuing onto the following pages when the header closes its page
// (footers are not yet stripped at detection time; a header as a page's
// last line puts the SENT line at the top of the next page). Lockstep with
// ofw.py _first_line_after.
function firstLineAfter(pages: string[], pageIndex: number, pos: number): string {
  for (let p = pageIndex; p < pages.length; p++) {
    const text = p === pageIndex ? pages[p].slice(pos) : pages[p];
    for (const ln of text.split("\n")) {
      const s = ln.trim();
      if (!s || FOOTER_RE.test(s) || BARE_FOOTER_RE.test(s)) continue;
      return s;
    }
  }
  return "";
}

// Drop the page footer. Per-message template: the branded line, wherever it
// sits. Redesigned templates (conversation-grouped and individual): the bare
// `Page N of M`, and only when it is the page's LAST non-empty line (1,420 of
// 1,420 pages on the first real export, 1,367 of 1,367 on the first ungrouped
// one). Lockstep with ofw.py _clean_pages.
function cleanPages(pages: string[], template: OfwTemplate): string[] {
  return pages.map((raw) => {
    const lines = raw.split("\n").filter((line) => !FOOTER_RE.test(line.trim()));
    if (INLINE_TEMPLATES.has(template)) {
      let last = lines.length - 1;
      while (last >= 0 && !lines[last].trim()) last -= 1;
      if (last >= 0 && BARE_FOOTER_RE.test(lines[last].trim())) {
        lines.splice(last, 1);
      }
    }
    return lines.join("\n");
  });
}

function concatenatePages(pages: string[]): string {
  return pages.join("\n");
}

// Collapse OFW's page-break line duplication. OFW renders body text that
// straddles a page break TWICE: once at the bottom of page N and again at
// the top of page N+1. After the footer is stripped, the copies are the
// trailing content lines of page N and the leading content lines of page
// N+1. The reprint can span more than one line (measured: up to two), so
// this collapses the maximal run where page N's last k content lines equal
// page N+1's first k. A page that begins a new message starts with a header,
// never a body line, so a boundary match can only occur inside one message's
// body. Never consumes every content line of page N+1 (a page that looks
// entirely like a reprint would otherwise vanish; one surviving duplicate is
// the safer failure). A genuine repeat split exactly by a page break is
// text-indistinguishable from the artifact and IS collapsed, so every
// collapsed line is returned for the audit trail. Lockstep with ofw.py
// _dedup_page_boundaries (Python has had this since 2026-06; the browser
// path got it 2026-10-28).
export function dedupPageBoundaries(pages: string[]): [string[], string[]] {
  if (pages.length === 0) return [pages, []];
  const out: string[] = [pages[0]];
  const collapsed: string[] = [];
  for (const page of pages.slice(1)) {
    const prevContent = out[out.length - 1]
      .split("\n")
      .map((ln) => ln.trim())
      .filter((ln) => ln);
    const curLines = page.split("\n");
    const curIdx: number[] = [];
    curLines.forEach((ln, i) => {
      if (ln.trim()) curIdx.push(i);
    });
    const curContent = curIdx.map((i) => curLines[i].trim());
    const limit = Math.min(prevContent.length, curContent.length - 1);
    let run = 0;
    for (let k = 1; k <= limit; k++) {
      let equal = true;
      for (let j = 0; j < k; j++) {
        if (prevContent[prevContent.length - k + j] !== curContent[j]) {
          equal = false;
          break;
        }
      }
      if (equal) run = k;
    }
    // A header never straddles a page break (a page's new message starts
    // with the header itself), so a boundary run holding a header-shaped
    // line is not the reprint artifact: it is a typed line colliding with
    // the next page's real header. Both copies stay for the header-sequence
    // guard, which refuses the file rather than choosing. Lockstep with
    // ofw.py.
    if (run > 0 && curContent.slice(0, run).some((ln) => MSG_HEADER_ANY_RE.test(ln))) {
      run = 0;
    }
    let kept = page;
    if (run > 0) {
      collapsed.push(...curContent.slice(0, run));
      const drop = new Set(curIdx.slice(0, run));
      kept = curLines.filter((_, i) => !drop.has(i)).join("\n");
    }
    out.push(kept);
  }
  return [out, collapsed];
}

// --- Cover page ---

// Contact-column residue that can ride on the end of a cover value line: a
// phone number (`+1 (866) 755-9991`), an email address, a bare web domain.
// Stripped token by token from the end (linear; a regex with a nested
// quantifier here backtracked exponentially on a crafted name). Lockstep
// with ofw.py _strip_contact_suffix.
const EMAIL_TOKEN_RE = /^\S+@\S+$/;
const DOMAIN_TOKEN_RE = /^(?:www\.)?[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.(?:com|org|net)$/i;
const PHONE_TOKEN_RE = /^[+()\d.-]+$/;
// A run of phone-shaped tokens is a phone number only with this many
// digits; a short number inside a display name stays.
const PHONE_MIN_DIGITS = 7;

// Cover labels (and vendor boilerplate) that a value lookahead can land on —
// never a parent's or child's name. Lockstep with ofw.py.
const COVER_LABEL_RE =
  /^(?:Report Details|Date Range:|Contains:|Order:|Sort Order:|Format:|Generated:|Timezone:|Parents:|Child\(ren\):|Message \d+ of|Conversation \d+)/;
const VENDOR_TOKEN_RE = /ourfamilywizard|\bLLC\b/i;
// Any of these on page 1 means a cover is present (and must then parse).
const COVER_MARKER_RE = /^(?:Timezone:|Parents:|Contains:)/m;

function countDigits(s: string): number {
  let n = 0;
  for (const ch of s) if (ch >= "0" && ch <= "9") n += 1;
  return n;
}

// Drop trailing contact-column tokens from a cover value line: an email or
// a bare domain goes; a run of phone-shaped tokens goes only when it carries
// at least PHONE_MIN_DIGITS digits. Everything before the first removed
// token is returned untouched. Lockstep with ofw.py _strip_contact_suffix.
export function stripContactSuffix(value: string): string {
  const spans: [number, number][] = [];
  for (const m of value.matchAll(/\S+/g)) {
    spans.push([m.index!, m.index! + m[0].length]);
  }
  let keep = spans.length;
  while (keep > 0) {
    const tok = value.slice(spans[keep - 1][0], spans[keep - 1][1]);
    if (EMAIL_TOKEN_RE.test(tok) || DOMAIN_TOKEN_RE.test(tok)) {
      keep -= 1;
      continue;
    }
    if (PHONE_TOKEN_RE.test(tok) && countDigits(tok) > 0) {
      let run = 0;
      let digits = 0;
      while (run < keep) {
        const t = value.slice(spans[keep - 1 - run][0], spans[keep - 1 - run][1]);
        if (!PHONE_TOKEN_RE.test(t)) break;
        digits += countDigits(t);
        run += 1;
      }
      if (digits >= PHONE_MIN_DIGITS) {
        keep -= run;
        continue;
      }
    }
    break;
  }
  return keep > 0 ? value.slice(0, spans[keep - 1][1]) : "";
}

// [labelFound, candidate values] for a cover label: the inline remainder of
// the label line first, then up to `lookahead` following non-empty lines.
// The two extractors disagree on where a cover value sits (pdfplumber: the
// contact column merges onto the label line and the value drops to the next
// line; pdf.js: value inline, contact column on its own line), so callers
// pick the first candidate that looks like the value they want. Lockstep
// with ofw.py _cover_value.
function coverValue(
  lines: string[],
  labelRe: RegExp,
  lookahead: number,
): [boolean, string[]] {
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].trim().match(labelRe);
    if (!m) continue;
    const cands = [m[1].trim()];
    for (let j = i + 1; j < lines.length; j++) {
      const v = lines[j].trim();
      if (v) cands.push(v);
      if (cands.length > lookahead) break;
    }
    return [true, cands];
  }
  return [false, []];
}

// Accepts both report templates and both extractor shapes: the Parents:
// value is the first candidate that, with any contact-column residue
// stripped, is neither another cover label nor vendor boilerplate and holds
// a comma (two names); none → throws, never a guess. Child(ren): is the
// inline value, else the next line unless that is another cover label or
// contact detail; Contains: tolerates a thousands separator and the optional
// `across C conversations` tail. Lockstep with ofw.py _parse_cover.
function parseCover(pageText: string): CoverInfo {
  const lines = pageText.split("\n");
  const tzMatch = pageText.match(/^Timezone:\s*(\S+)/m);
  const [parentsFound, parentCands] = coverValue(lines, /^Parents:(.*)$/, 2);
  const [childrenFound, childCands] = coverValue(lines, /^Child\(ren\):(.*)$/, 1);
  const totalMatch = pageText.match(
    /^Contains:\s*([\d,]+)\s+selected messages(?:\s+across\s+([\d,]+)\s+conversations)?/m,
  );

  if (!tzMatch) throw new MalformedOFWError("cover page missing Timezone");
  if (!parentsFound) throw new MalformedOFWError("cover page missing Parents");
  if (!totalMatch)
    throw new MalformedOFWError(
      "cover page missing Contains: N selected messages",
    );

  // A contact-column token (the support phone, the info@ address, the
  // vendor's domain) can ride on the end of the names line, or the whole
  // contact line can sit where the names are expected, depending on the
  // extractor's row tolerance. The lookahead can also reach the next cover
  // label (`Child(ren):` under pdf.js) or the `OurFamilyWizard, LLC.` line.
  // None of those is ever the value; with no acceptable candidate the cover
  // is unreadable and says so.
  const parentsRaw = parentCands
    .map((c) => stripContactSuffix(c).trim())
    .find(
      (c) =>
        c.length > 0 &&
        c.includes(",") &&
        !COVER_LABEL_RE.test(c) &&
        !VENDOR_TOKEN_RE.test(c),
    );
  if (parentsRaw === undefined) {
    throw new MalformedOFWError(
      "cover page Parents: expected exactly 2 parents, comma-separated, at or below the label; found no such line",
    );
  }
  const parents = parentsRaw
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p);
  if (parents.length !== 2) {
    throw new MalformedOFWError(
      `expected exactly 2 parents on cover page; got ${parents.length}: ${parents.join(", ")}`,
    );
  }
  let children: string[] = [];
  if (childrenFound) {
    let raw = stripContactSuffix(childCands[0]).trim();
    if (!raw && childCands.length > 1) {
      const nxt = stripContactSuffix(childCands[1]).trim();
      if (nxt && !COVER_LABEL_RE.test(nxt) && !/[\d@]/.test(nxt)) raw = nxt;
    }
    children = raw
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c);
  }
  const conv = totalMatch[2];
  return {
    timezone_name: tzMatch[1].trim(),
    party_a: parents[0],
    party_b: parents[1],
    total_messages: parseInt(totalMatch[1].replace(/,/g, ""), 10),
    children,
    conversation_count: conv ? parseInt(conv.replace(/,/g, ""), 10) : null,
  };
}

// Derive (party_a, party_b) by `From:` send frequency. Mirrors the Python
// civil_communicator _determine_declared_parties (and ofw.py's port of it)
// so cover-less OFW exports derive parties the same way: the two most
// frequent senders, ties broken by first-seen order.
function determineDeclaredParties(
  parsed: ParsedMessage[],
): [string, string] {
  const counts = new Map<string, number>();
  const firstSeen = new Map<string, number>();
  parsed.forEach((m, idx) => {
    counts.set(m.from_name, (counts.get(m.from_name) ?? 0) + 1);
    if (!firstSeen.has(m.from_name)) firstSeen.set(m.from_name, idx);
  });
  if (counts.size === 0) return ["", ""];
  const ordered = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return (firstSeen.get(a[0]) ?? 0) - (firstSeen.get(b[0]) ?? 0);
  });
  const partyA = ordered.length >= 1 ? ordered[0][0] : "";
  const partyB = ordered.length >= 2 ? ordered[1][0] : "";
  return [partyA, partyB];
}

// Build a CoverInfo for a cover-less export from the parsed messages:
// parties by frequency, children unknown ([]), timezone assumed (caller
// flags the assumption). Lockstep with ofw.py _synthesize_cover.
function synthesizeCover(
  parsed: ParsedMessage[],
  timezoneName: string,
): CoverInfo {
  const [partyA, partyB] = determineDeclaredParties(parsed);
  return {
    timezone_name: timezoneName,
    party_a: partyA,
    party_b: partyB,
    total_messages: parsed.length,
    children: [],
    conversation_count: null,
  };
}

// --- Stage 3: split stanzas on "Message N of M" ---

// Drop a trailing `Conversation <c>` separator line from a stanza. The
// conversation template prints the separator just before the next
// conversation's first header, so after splitting on headers it lands at
// the END of the previous stanza. Only the exact index of the FOLLOWING
// header's conversation is removed — a body line reading `Conversation 7`
// in the middle of a message stays. Lockstep with ofw.py.
function stripTrailingSeparator(text: string, conversationIndex: number): string {
  const stripped = text.replace(/\s+$/, "");
  const nl = stripped.lastIndexOf("\n");
  const last = nl >= 0 ? stripped.slice(nl + 1) : stripped;
  const m = last.trim().match(CONVERSATION_SEP_RE);
  if (m && parseInt(m[1], 10) === conversationIndex) {
    return nl >= 0 ? stripped.slice(0, nl) : "";
  }
  return text;
}

// Accepts header lines only in OFW's own numbering order. OFW prints what a
// party typed verbatim, so a line shaped exactly like a message header can
// sit inside a body — and, followed by a typed `SENT … FROM <other parent>`
// block, would otherwise become a message attributed to the other parent.
// Every real OFW export surveyed (eleven files, both templates, up to 4,666
// pages) numbers its headers contiguously: per-message `Message N of M`
// runs N = 1, 2, 3 … with M constant; the conversation template's `n of k`
// runs 1..k inside each conversation, `c of C` steps by one, C and k are
// constant. A header that breaks that sequence is therefore typed text and
// stays in the body it was typed into (reported, never dropped). The first
// header anchors the sequence: it precedes every body, so no typed line can
// reach it.
//
// The one shape the sequence cannot settle: a second header claiming the
// position just accepted. Either a party typed the exact next position
// inside the message before it (the real header then follows) or typed
// their own position inside their own message; both read the same, and
// choosing either attributes one party's words to the other. That case
// refuses the file with a message naming the position. Lockstep with
// ofw.py _HeaderSequence.
class HeaderSequence {
  private prev: number[] | null = null;
  readonly anomalies: string[] = [];

  constructor(private readonly template: OfwTemplate) {}

  // null when `m` is the next header in sequence, else why it is not.
  accept(m: RegExpMatchArray): string | null {
    if (this.template === TEMPLATE_CONVERSATION) {
      const cur = [1, 2, 3, 4].map((i) => parseInt(m[i], 10));
      const [n, k, c, total] = cur;
      if (this.prev === null) {
        if (n !== 1 || c !== 1) {
          this.anomalies.push(
            `first header is message ${n} of ${k} in conversation ${c} of ${total}; a complete export starts at 1 of k in conversation 1`,
          );
        }
        this.prev = cur;
        return null;
      }
      const [pn, pk, pc, ptotal] = this.prev;
      if (n === pn && k === pk && c === pc && total === ptotal) {
        HeaderSequence.refuseRepeat(m);
      }
      if (n > k || c > total) {
        return `message ${n} of ${k} in conversation ${c} of ${total} is past the end of its run`;
      }
      if (total !== ptotal) return `expected ${ptotal} conversations, got ${total}`;
      if (c === pc) {
        if (n !== pn + 1 || k !== pk) {
          return `expected message ${pn + 1} of ${pk} in conversation ${pc}`;
        }
      } else if (c === pc + 1) {
        if (n !== 1) return `expected message 1 of k in conversation ${c}`;
        if (pn !== pk) {
          this.anomalies.push(`conversation ${pc} ended at message ${pn} of ${pk}`);
        }
      } else {
        return `expected conversation ${pc} or ${pc + 1}, got ${c}`;
      }
      this.prev = cur;
      return null;
    }
    const cur = [parseInt(m[1], 10), parseInt(m[2], 10)];
    const [num, total] = cur;
    if (this.prev === null) {
      if (num !== 1) {
        this.anomalies.push(
          `first header is message ${num} of ${total}; a complete export starts at 1`,
        );
      }
      this.prev = cur;
      return null;
    }
    const [pnum, ptotal] = this.prev;
    if (num === pnum && total === ptotal) HeaderSequence.refuseRepeat(m);
    if (num > total) return `message ${num} of ${total} is past the export's last message`;
    if (total !== ptotal || num !== pnum + 1) {
      return `expected message ${pnum + 1} of ${ptotal}`;
    }
    this.prev = cur;
    return null;
  }

  // Anomalies seen, plus an unfinished last conversation.
  close(): string[] {
    if (this.template === TEMPLATE_CONVERSATION && this.prev !== null) {
      const [pn, pk, pc] = this.prev;
      if (pn !== pk) this.anomalies.push(`conversation ${pc} ended at message ${pn} of ${pk}`);
    }
    return this.anomalies;
  }

  private static refuseRepeat(m: RegExpMatchArray): never {
    const header = m[0].split(/\s+/).join(" ").trim();
    throw new MalformedOFWError(
      `two header lines claim the same position (${header}): a message header was typed inside a message, or the export is damaged. The parser cannot tell which is the real one without attributing one party's words to the other, so this export needs review; re-exporting with a different date range changes the numbering and separates the two.`,
    );
  }
}

export interface SplitResult {
  stanzas: Stanza[];
  // One entry per header-shaped line that failed the sequence check and was
  // kept as body text: the message_number it stayed in, the line, why.
  keptLines: { message_number: number; line: string; reason: string }[];
  // Sequence facts worth a loud warning (nothing was changed).
  anomalies: string[];
}

// Split combined text into per-message stanzas on the header line. Only the
// detected template's own header shape splits, and only headers that
// continue OFW's numbering sequence (HeaderSequence); a header-shaped line
// that does not is body text and is reported in `keptLines`. The cover
// content before the first header is discarded, and the conversation
// template's `Conversation N` separator is removed from the stanza it trails
// at a conversation change. Lockstep with ofw.py _split_stanzas.
export function splitStanzas(combined: string, template: OfwTemplate): SplitResult {
  const conversation = template === TEMPLATE_CONVERSATION;
  // We need both the match positions AND the captured groups; .matchAll
  // gives us both.
  const matches = Array.from(combined.matchAll(headerRe(template)));
  if (matches.length === 0) return { stanzas: [], keptLines: [], anomalies: [] };
  const seq = new HeaderSequence(template);
  const accepted: RegExpMatchArray[] = [];
  const keptLines: SplitResult["keptLines"] = [];
  for (const m of matches) {
    const reason = seq.accept(m);
    if (reason === null) {
      accepted.push(m);
    } else {
      const number = conversation
        ? accepted.length
        : parseInt(accepted[accepted.length - 1][1], 10);
      keptLines.push({
        message_number: number,
        line: m[0].split(/\s+/).join(" ").trim(),
        reason,
      });
    }
  }

  const out: Stanza[] = [];
  for (let i = 0; i < accepted.length; i++) {
    const m = accepted[i];
    const start = (m.index ?? 0) + m[0].length;
    const nxt = i + 1 < accepted.length ? accepted[i + 1] : null;
    const end = nxt ? (nxt.index ?? combined.length) : combined.length;
    let text = combined.slice(start, end);
    if (conversation) {
      const c = parseInt(m[3], 10);
      if (nxt && parseInt(nxt[3], 10) === c + 1) {
        text = stripTrailingSeparator(text, c + 1);
      }
      out.push({
        message_number: out.length + 1,
        text: text.trim(),
        conversation_index: c,
        index_in_conversation: parseInt(m[1], 10),
        conversation_size: parseInt(m[2], 10),
      });
    } else {
      out.push({ message_number: parseInt(m[1], 10), text: text.trim() });
    }
  }
  return { stanzas: out, keptLines, anomalies: seq.close() };
}

// --- Stage 4: parse stanza fields ---

// A Sent value that is not an OFW datetime makes the STANZA malformed:
// checked at parse time so the message is skipped under the malformed
// tolerance (and warned about) instead of aborting the whole ingest later,
// when the timestamp is converted. Lockstep with ofw.py _require_datetime.
function requireDatetime(msgNum: number, sentRaw: string): void {
  if (!DATETIME_RE.test(sentRaw)) {
    throw new MalformedOFWError(
      `message ${msgNum}: unparseable Sent datetime: ${JSON.stringify(sentRaw)}`,
    );
  }
}

function nextNonempty(lines: string[], start: number): string | null {
  for (let j = start; j < lines.length; j++) {
    const v = lines[j].trim();
    if (v) return v;
  }
  return null;
}

function nextNonemptyIdx(lines: string[], start: number): number | null {
  for (let j = start; j < lines.length; j++) {
    if (lines[j].trim()) return j;
  }
  return null;
}

// OFW renders headers in two layouts: label-alone (value on the next line,
// e.g. `From:` then `Quinn Morales`) or inline (`From: Quinn Morales`).
// Some exports mix the two within one file (a short value wraps onto the
// label line). Returns the inline value for an inline anchor, "" for a
// label-alone anchor, or null when the line isn't this label's anchor.
// Lockstep with ofw.py _label_value_or_anchor.
function labelAnchor(line: string, label: string): string | null {
  const s = line.trim();
  if (s === label) return "";
  if (s.startsWith(label + " ")) return s.slice(label.length).trim();
  return null;
}

// Find the next `label` anchor at/after `start`; return [idx, inlineValue]
// (inlineValue is "" for a label-alone anchor) or null.
function headerField(
  lines: string[],
  label: string,
  start: number,
): [number, string] | null {
  for (let j = start; j < lines.length; j++) {
    const inline = labelAnchor(lines[j], label);
    if (inline !== null) return [j, inline];
  }
  return null;
}

// `template` selects the header grammar: the per-message report's
// `Sent:/From:/To:/Subject:` labels (below), or the conversation report's
// inline `SENT … FROM` / `TO` / `SUBJECT` trio (parseStanzaConversation).
// The body/attachments tail is shared. Lockstep with ofw.py _parse_stanza.
function parseStanza(
  msgNum: number,
  stanza: string,
  template: OfwTemplate = TEMPLATE_PER_MESSAGE,
): ParsedMessage {
  if (INLINE_TEMPLATES.has(template)) {
    return parseStanzaConversation(msgNum, stanza);
  }
  const lines = stanza.split("\n");

  // Sent: anchors the header; first occurrence wins. Accept inline or
  // label-alone, then everything after Subject is body — so a quoted email
  // thread in the body (with its own Sent/From/To/Subject) isn't read as
  // the header. Lockstep with ofw.py _parse_stanza.
  let sentIdx: number | null = null;
  let sentRaw = "";
  for (let i = 0; i < lines.length; i++) {
    const inline = labelAnchor(lines[i], "Sent:");
    if (inline !== null) {
      sentIdx = i;
      sentRaw = (inline !== "" ? inline : nextNonempty(lines, i + 1) ?? "").trim();
      break;
    }
  }
  if (sentIdx === null || !sentRaw) {
    throw new MalformedOFWError(`message ${msgNum}: missing Sent:`);
  }
  requireDatetime(msgNum, sentRaw);

  const fromHit = headerField(lines, "From:", sentIdx + 1);
  const toHit = fromHit ? headerField(lines, "To:", fromHit[0] + 1) : null;
  const subjHit = toHit ? headerField(lines, "Subject:", toHit[0] + 1) : null;
  if (fromHit === null || toHit === null || subjHit === null) {
    throw new MalformedOFWError(
      `message ${msgNum}: missing From/To/Subject labels`,
    );
  }
  const [fromIdx, fromInline] = fromHit;
  const [toIdx, toInline] = toHit;
  const [subjIdx, subjInline] = subjHit;

  const fromName = fromInline !== "" ? fromInline : nextNonempty(lines, fromIdx + 1);
  if (!fromName) {
    throw new MalformedOFWError(`message ${msgNum}: empty From name`);
  }

  const toLines: string[] = [];
  if (toInline !== "") toLines.push(toInline);
  for (let j = toIdx + 1; j < subjIdx; j++) {
    const v = lines[j].trim();
    if (v) toLines.push(v);
  }
  if (toLines.length === 0) {
    throw new MalformedOFWError(`message ${msgNum}: no To: recipients`);
  }

  let subject: string;
  let bodyStart: number;
  if (subjInline !== "") {
    subject = subjInline;
    bodyStart = subjIdx + 1;
  } else {
    subject = nextNonempty(lines, subjIdx + 1) ?? "";
    const subjLineIdx = nextNonemptyIdx(lines, subjIdx + 1);
    bodyStart = subjLineIdx !== null ? subjLineIdx + 1 : subjIdx + 2;
  }

  const [bodyText, attachmentsRaw] = splitBodyAndAttachments(
    lines.slice(bodyStart),
    TEMPLATE_PER_MESSAGE,
  );

  return {
    message_number: msgNum,
    sent_raw: sentRaw,
    from_name: fromName,
    to_lines: toLines,
    subject,
    body: bodyText,
    attachments_raw: attachmentsRaw,
  };
}

// Separate the message's OWN `See Attachments:` payload from its body.
// Per-message template: the own attachments line is the FIRST one found
// before any quoted history (a quoted message's attachments belong to that
// quoted message and stay in the body), and its payload runs to the
// quoted-history boundary. Conversation template: there is no quoted
// history, OFW prints the attachments line LAST, so the last anchor is the
// own one and its payload runs to the end of the stanza — a typed
// `See Attachments:` line higher up is body text, and a pasted email header
// cannot hide the real line. Either way the payload is extracted only when
// it parses fully as a `name (size)` list; anything else stays in the body
// verbatim. Lockstep with ofw.py _split_body_and_attachments.
function splitBodyAndAttachments(
  bodyLines: string[],
  template: OfwTemplate,
): [string, string | null] {
  let quotedStart: number | null;
  let ownEnd: number;
  let candidates: number[];
  if (INLINE_TEMPLATES.has(template)) {
    quotedStart = null;
    ownEnd = bodyLines.length;
    candidates = [];
    for (let j = ownEnd - 1; j >= 0; j--) candidates.push(j);
  } else {
    quotedStart = findQuotedHeaderStart(bodyLines);
    ownEnd = quotedStart !== null ? quotedStart : bodyLines.length;
    candidates = [];
    for (let j = 0; j < ownEnd; j++) candidates.push(j);
  }

  let attachmentsRaw: string | null = null;
  let attIdx: number | null = null;
  let attInline = "";
  for (const j of candidates) {
    const inline = labelAnchor(bodyLines[j], "See Attachments:");
    if (inline !== null) {
      attIdx = j;
      attInline = inline;
      break;
    }
  }
  let keptLines = bodyLines;
  if (attIdx !== null) {
    const payloadLines = (attInline ? [attInline] : []).concat(
      bodyLines.slice(attIdx + 1, ownEnd),
    );
    const payload = payloadLines.join("\n").trim();
    if (payload && isAttachmentList(payload)) {
      attachmentsRaw = payload;
      keptLines = bodyLines
        .slice(0, attIdx)
        .concat(quotedStart !== null ? bodyLines.slice(quotedStart) : []);
    }
    // else: not a clean list — leave everything in the body; the fidelity
    // layer surfaces it for review.
  }
  return [keptLines.join("\n").trim(), attachmentsRaw];
}

// How many NON-EMPTY lines after the `SENT … FROM` row the conversation
// template's `SUBJECT` line may sit (one TO line per recipient, plus any
// monitor lines).
const CAPS_HEADER_HOP = 8;

// Parse a conversation-template stanza (header line already stripped):
//   SENT <datetime> FROM <sender>                (pdf.js may break this row
//                                                 after the datetime:
//                                                 `SENT <dt>` / `FROM <s>`)
//   TO <recipient> (First Viewed: <datetime>)   (one per recipient; First
//                                                 Viewed optional)
//   SUBJECT <subject>                            (may be empty)
//   <body> [See Attachments: …]
// This template carries no quoted history, so the body is the new text only
// and the attachments line is the stanza's last (splitBodyAndAttachments).
// Lockstep with ofw.py _parse_stanza_conversation.
function parseStanzaConversation(msgNum: number, stanza: string): ParsedMessage {
  const lines = stanza.split("\n");
  const first = nextNonemptyIdx(lines, 0);
  if (first === null) {
    throw new MalformedOFWError(`message ${msgNum}: empty stanza`);
  }
  let headerEnd = first;
  let sentRaw: string;
  let fromName: string;
  const m = lines[first].trim().match(SENT_FROM_RE);
  if (m) {
    sentRaw = m[1].trim();
    fromName = m[2].trim();
  } else {
    const sentOnly = lines[first].trim().match(SENT_ONLY_RE);
    const nxt = nextNonemptyIdx(lines, first + 1);
    const fromOnly =
      sentOnly && nxt !== null ? lines[nxt].trim().match(FROM_ONLY_RE) : null;
    if (!sentOnly || !fromOnly || nxt === null) {
      throw new MalformedOFWError(`message ${msgNum}: missing SENT … FROM header`);
    }
    sentRaw = sentOnly[1].trim();
    fromName = fromOnly[1].trim();
    headerEnd = nxt;
  }
  if (!sentRaw || !fromName) {
    throw new MalformedOFWError(`message ${msgNum}: empty SENT or FROM value`);
  }
  requireDatetime(msgNum, sentRaw);

  const toLines: string[] = [];
  let subjIdx: number | null = null;
  let subject = "";
  let hops = 0;
  for (let j = headerEnd + 1; j < lines.length; j++) {
    const s = lines[j].trim();
    if (!s) continue;
    hops += 1;
    if (hops > CAPS_HEADER_HOP) break;
    const sm = s.match(SUBJECT_CAPS_RE);
    if (sm) {
      subjIdx = j;
      subject = (sm[1] ?? "").trim();
      break;
    }
    const tm = s.match(TO_CAPS_RE);
    toLines.push(tm ? tm[1].trim() : s);
  }
  if (subjIdx === null) {
    throw new MalformedOFWError(`message ${msgNum}: missing SUBJECT line`);
  }
  if (toLines.length === 0) {
    throw new MalformedOFWError(`message ${msgNum}: no TO recipients`);
  }

  const [bodyText, attachmentsRaw] = splitBodyAndAttachments(
    lines.slice(subjIdx + 1),
    TEMPLATE_CONVERSATION,
  );
  return {
    message_number: msgNum,
    sent_raw: sentRaw,
    from_name: fromName,
    to_lines: toLines,
    subject,
    body: bodyText,
    attachments_raw: attachmentsRaw,
  };
}

// How far apart the labels of a quoted header may sit and still count as
// one contiguous Sent → From → To → Subject sequence. Label-alone rendering
// puts each value on its own line (and To: may list several recipients), so
// the window is per-hop. Lockstep with ofw.py _QUOTED_HEADER_HOP.
const QUOTED_HEADER_HOP = 6;

// Index of the first quoted-message header inside a body, else null. A
// `Sent:` anchor only counts when its value looks like an OFW datetime AND
// From:/To:/Subject: anchors follow in order, each within a few lines — so
// prose that merely starts with `Sent:` cannot open a phantom quote block.
function findQuotedHeaderStart(lines: string[]): number | null {
  for (let i = 0; i < lines.length; i++) {
    const inline = labelAnchor(lines[i], "Sent:");
    if (inline === null) continue;
    const sentVal = inline !== "" ? inline : nextNonempty(lines, i + 1);
    if (!sentVal || !DATETIME_RE.test(sentVal)) continue;
    let j = i;
    let ok = true;
    for (const label of ["From:", "To:", "Subject:"]) {
      let hit: number | null = null;
      const limit = Math.min(j + 1 + QUOTED_HEADER_HOP, lines.length);
      for (let k = j + 1; k < limit; k++) {
        if (labelAnchor(lines[k], label) !== null) {
          hit = k;
          break;
        }
      }
      if (hit === null) {
        ok = false;
        break;
      }
      j = hit;
    }
    if (ok) return i;
  }
  return null;
}

// True when the payload is nothing but `name (size)` items. Wrapped lists
// flatten to one line first, so a file name or size split across a PDF line
// break still parses. Any residue beyond commas and whitespace means the
// text is NOT a pure attachment list and must stay in the body.
// A real attachment list is a few hundred characters; the item scan is
// quadratic on a payload with no items, so anything past this is treated as
// prose and stays in the body. Lockstep with ofw.py _ATT_PAYLOAD_MAX_CHARS.
const ATT_PAYLOAD_MAX_CHARS = 20_000;

function isAttachmentList(payload: string): boolean {
  if (payload.length > ATT_PAYLOAD_MAX_CHARS) return false;
  const flat = payload.replace(/\s+/g, " ").trim();
  let pos = 0;
  let residue = "";
  let any = false;
  for (const m of flat.matchAll(ATT_ITEM_RE)) {
    any = true;
    residue += flat.slice(pos, m.index!);
    pos = m.index! + m[0].length;
  }
  residue += flat.slice(pos);
  if (!any) return false;
  return /^[\s,]*$/.test(residue);
}

// --- To-line: split recipient + first-viewed ---

function parseToLine(line: string): [string, string | null] {
  const m = line.match(FIRST_VIEWED_RE);
  if (m) {
    const name = line.slice(0, m.index!).trim();
    return [name, m[1].trim()];
  }
  return [line.trim(), null];
}

// --- Timestamp ---

function parseLocalDatetimeToUtc(raw: string, tzName: string): string {
  const m = raw.match(DATETIME_RE);
  if (!m) throw new MalformedOFWError(`unparseable datetime: ${JSON.stringify(raw)}`);
  const [, monthS, dayS, yearS, hourS, minuteS, meridian] = m;
  let hour = parseInt(hourS, 10);
  if (meridian.toUpperCase() === "PM" && hour !== 12) hour += 12;
  else if (meridian.toUpperCase() === "AM" && hour === 12) hour = 0;

  if (!IANAZone.isValidZone(tzName)) {
    throw new MalformedOFWError(`unknown timezone: ${tzName}`);
  }
  const local = DateTime.fromObject(
    {
      year: parseInt(yearS, 10),
      month: parseInt(monthS, 10),
      day: parseInt(dayS, 10),
      hour,
      minute: parseInt(minuteS, 10),
      second: 0,
    },
    { zone: tzName },
  );
  if (!local.isValid) {
    throw new MalformedOFWError(
      `bad datetime ${raw} in ${tzName}: ${local.invalidExplanation ?? local.invalidReason}`,
    );
  }
  // ISO-Z, second precision — match the Python adapter's strftime.
  return local.toUTC().toFormat("yyyy-LL-dd'T'HH:mm:ss'Z'");
}

// --- Attachments ---

function parseAttachments(
  raw: string | null,
): [string, { filename: string; size: string }[]] {
  if (!raw) return ["", []];
  const flat = raw.replace(/\s+/g, " ").trim();
  const items: { filename: string; size: string }[] = [];
  for (const m of flat.matchAll(ATT_ITEM_RE)) {
    const name = m[1].trim().replace(/,+$/, "").trim();
    const size = m[2].trim();
    items.push({ filename: name, size });
  }
  if (items.length === 0) return ["", []];
  const names = items.map((i) => i.filename).join(", ");
  const summary = `${items.length} attachment${items.length === 1 ? "" : "s"}: ${names}`;
  return [summary, items];
}

// --- Stage 5: build rows + metadata + stats ---

function buildRowsAndMetadata(
  parsed: ParsedMessage[],
  cover: CoverInfo,
  timezoneAssumed: boolean,
  template: OfwTemplate = TEMPLATE_PER_MESSAGE,
): { rows: CanonicalRow[]; metas: SourceMetadata[]; stats: IngestStats } {
  const declaredSet = new Set([cover.party_a, cover.party_b]);
  const outOfSet: string[] = [];
  let nonPrintablesStripped = 0;

  const rows: CanonicalRow[] = [];
  const metas: SourceMetadata[] = [];

  for (const m of parsed) {
    const [sender, nSender] = sanitizeActor(m.from_name);
    nonPrintablesStripped += nSender;
    let recipient = "";
    if (declaredSet.has(sender)) {
      recipient = sender === cover.party_a ? cover.party_b : cover.party_a;
    } else if (!outOfSet.includes(sender)) {
      outOfSet.push(sender);
    }

    const firstViewedBy: Record<string, string> = {};
    for (const line of m.to_lines) {
      const [name, fv] = parseToLine(line);
      if (fv) firstViewedBy[name] = fv;
    }

    const tsUtc = parseLocalDatetimeToUtc(m.sent_raw, cover.timezone_name);
    const [body, nBody] = sanitizeBody(m.body);
    nonPrintablesStripped += nBody;
    const [attSummary, attStructured] = parseAttachments(m.attachments_raw);

    rows.push({
      timestamp: tsUtc,
      sender,
      recipient,
      subject: m.subject,
      body,
      attachments: attSummary,
    });
    const meta: SourceMetadata = {
      source: ADAPTER_NAME,
      message_number: m.message_number,
      to_raw: [...m.to_lines],
      first_viewed_by: firstViewedBy,
      subject: m.subject,
      attachments_structured: attStructured,
    };
    // Conversation template only — absent on the per-message template so
    // its metadata shape is unchanged. Lockstep with ofw.py.
    if (m.conversation_index !== undefined) {
      meta.conversation_index = m.conversation_index;
      meta.index_in_conversation = m.index_in_conversation;
    }
    metas.push(meta);
  }

  // Sort by timestamp ascending (OFW is chronological but defend anyway).
  const pairs = rows.map((r, i) => [r, metas[i]] as const);
  pairs.sort((a, b) => (a[0].timestamp < b[0].timestamp ? -1 : a[0].timestamp > b[0].timestamp ? 1 : 0));
  const sortedRows = pairs.map(([r]) => r);
  const sortedMetas = pairs.map(([, m]) => m);

  const perPartyTotals: Record<string, number> = {};
  for (const r of sortedRows) {
    if (declaredSet.has(r.sender)) {
      perPartyTotals[r.sender] = (perPartyTotals[r.sender] ?? 0) + 1;
    }
  }
  const rejectionRateByParty: Record<string, number> = {};
  for (const name of Object.keys(perPartyTotals)) {
    rejectionRateByParty[name] = 0.0;
  }

  const dateRange = {
    start: sortedRows.length > 0 ? sortedRows[0].timestamp : null,
    end:
      sortedRows.length > 0 ? sortedRows[sortedRows.length - 1].timestamp : null,
  };

  // Conversation count: the cover's declared figure when present (and not
  // zero — `||`, like Python's `or`), else the highest conversation index
  // seen; null on the per-message template.
  let conversationCount: number | null = null;
  if (template === TEMPLATE_CONVERSATION) {
    const highest = parsed.reduce(
      (acc, m) => Math.max(acc, m.conversation_index ?? 0),
      0,
    );
    conversationCount = cover.conversation_count || (highest > 0 ? highest : null);
  }

  return {
    rows: sortedRows,
    metas: sortedMetas,
    stats: {
      party_a: cover.party_a,
      party_b: cover.party_b,
      out_of_set_senders: outOfSet,
      message_count: sortedRows.length,
      accepted_count: sortedRows.length,
      rejected_count: 0,
      non_printables_stripped: nonPrintablesStripped,
      rejection_rate_by_party: rejectionRateByParty,
      date_range: dateRange,
      timezone: cover.timezone_name,
      children: cover.children,
      template,
      conversation_count: conversationCount,
      timezone_assumed: timezoneAssumed,
    },
  };
}

// --- CSV serialization ---

const CANONICAL_HEADERS = [
  "timestamp",
  "sender",
  "recipient",
  "subject",
  "body",
  "attachments",
] as const;

function csvEscape(cell: string): string {
  // RFC 4180: quote if contains comma, quote, or newline; double inner quotes.
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

function rowsToCsv(rows: CanonicalRow[]): string {
  const header = CANONICAL_HEADERS.join(",") + "\n";
  const body = rows
    .map((r) =>
      CANONICAL_HEADERS.map((h) => csvEscape(r[h])).join(","),
    )
    .join("\n");
  return rows.length > 0 ? header + body + "\n" : header;
}

// --- Case meta builder ---

const TODO_NOTES_SENTINEL = "TODO: optional freeform notes about this corpus";

function formatTimePeriod(startIso: string, endIso: string): string {
  if (!startIso || !endIso) return "";
  const fmt = (iso: string) =>
    DateTime.fromISO(iso, { zone: "utc" }).toFormat("LLL yyyy");
  const a = fmt(startIso);
  const b = fmt(endIso);
  return a === b ? a : `${a} – ${b}`;
}

const NUM_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];

// Auto-generate a usable `context` string for prompt_vars from the
// children list off the cover page. Mirrors the natural phrasing the
// desktop ingest wizard ends up with ("their two shared children" /
// "their child Mia"), so the chain's TODO-sentinel guard passes
// without forcing the cloud user through a separate wizard step.
//
// The desktop's Python adapter ships a TODO sentinel and relies on the
// in-app wizard to fill this in. Browser cloud has no wizard, so we
// auto-derive here.
function deriveContext(children: string[]): string {
  if (children.length === 0) {
    return "their co-parenting relationship";
  }
  const firsts = children.map((c) => c.trim().split(/\s+/)[0]).filter(Boolean);
  if (firsts.length === 1) return `their child ${firsts[0]}`;
  if (firsts.length === 2) return `their two children ${firsts[0]} and ${firsts[1]}`;
  const word = firsts.length < NUM_WORDS.length ? NUM_WORDS[firsts.length] : String(firsts.length);
  const tail = firsts.slice(0, -1).join(", ") + ", and " + firsts[firsts.length - 1];
  return `their ${word} children, ${tail}`;
}

// prompt_vars.format per template. Lockstep with ofw.py FORMAT_LABEL.
export const FORMAT_LABEL: Record<OfwTemplate, string> = {
  "per-message":
    "OurFamilyWizard Message Report PDF, canonicalized to CSV (schema v1)",
  "conversation-grouped":
    "OurFamilyWizard Messages Report PDF (grouped by conversation), canonicalized to CSV (schema v1)",
  "individual-messages":
    "OurFamilyWizard Messages Report PDF (individual messages, redesigned layout), canonicalized to CSV (schema v1)",
};

function buildCaseMeta(corpusId: string, stats: IngestStats): CaseMeta {
  const meta: CaseMeta = {
    id: corpusId,
    privacy_class: "private",
    prompt_vars: {
      party_a: stats.party_a,
      party_b: stats.party_b,
      format: FORMAT_LABEL[stats.template ?? TEMPLATE_PER_MESSAGE],
      time_period: formatTimePeriod(
        stats.date_range.start ?? "",
        stats.date_range.end ?? "",
      ),
      context: deriveContext(stats.children),
    },
    known_patterns: [],
    notes: TODO_NOTES_SENTINEL,
    ingestion: {
      page_count: stats.page_count,
      template: stats.template,
      conversation_count: stats.conversation_count,
    },
  };
  // Cover-less exports assumed the zone — surface it loudly. Absent (not
  // false) when a real cover was present, so cover-bearing meta is unchanged.
  if (stats.timezone_assumed) {
    meta.timezone_assumed = true;
  }
  return meta;
}

// --- Public entry point ---

export interface IngestOptions {
  /** Slug for the case (sets meta.id). Required by the cloud-server. */
  corpusId: string;
  /** Optional progress callback (per-page during pdf extraction). */
  onProgress?: (p: PdfExtractProgress) => void;
  /**
   * Timezone used ONLY for cover-less exports (page 1 starts at
   * `Message 1 of N`, no cover/summary page carrying a Timezone: header).
   * Such exports carry no zone, so we fall back to this (or America/New_York
   * when not supplied) and flag the assumption loudly. Ignored when a cover
   * IS present. Lockstep with ofw.py's default_timezone.
   */
  defaultTimezone?: string;
}

// Detect a cover-less export: page 1 carries no cover field at all (no
// Timezone/Parents/Contains), OR messages start on page 1 AHEAD of any cover
// fields (the `Message N of M` header is the first OFW marker on the page).
// A normal export carries the cover fields first and Message 1 on the same
// page, so that case stays cover-bearing. A cover that IS there but will not
// parse is an error, not a cover-less export: silently assuming a zone and
// dropping the declared parties and children would misread the export's
// own facts. Lockstep with ofw.py.
function resolveCover(page1: string, template: OfwTemplate): CoverInfo | null {
  const re = new RegExp(headerRe(template).source, "m");
  const msgOnPage1 = page1.match(re);
  const tzOnPage1 = page1.match(/^Timezone:\s*\S+/m);
  const messageLeadsPage1 =
    msgOnPage1 != null &&
    (tzOnPage1 == null ||
      (msgOnPage1.index ?? 0) < (tzOnPage1.index ?? 0));
  if (messageLeadsPage1) return null;
  if (!COVER_MARKER_RE.test(page1)) return null;
  try {
    return parseCover(page1);
  } catch (e) {
    if (e instanceof MalformedOFWError) {
      throw new MalformedOFWError(
        `OFW cover page present but unreadable (${e.message}). Not treating this export as cover-less: its timezone and parties must come from the cover, not be assumed.`,
      );
    }
    throw e;
  }
}

/**
 * Pure-text parsing path: takes already-extracted page texts and returns the
 * canonical IngestResult. Exposed so unit tests can exercise the parser
 * (including the cover-less path) without running pdf.js in Node. The PDF
 * extraction step is the same shared path used by the CC adapter.
 *
 * Throws MalformedOFWError if no `Message N of M` stanzas are present.
 */
export function ingestOfwFromPages(
  pagesRaw: string[],
  opts: { corpusId: string; defaultTimezone?: string },
): IngestResult {
  // The template is read off the raw header shape first, so the bare
  // `Page N of M` footer is stripped only from the layout that has it.
  const template = detectTemplate(pagesRaw);
  const cleaned = cleanPages(pagesRaw, template);
  if (cleaned.length === 0) {
    throw new MalformedOFWError("PDF contained no extractable text");
  }
  // Collapse OFW's page-break line duplication before stanza parsing so the
  // artifact never reaches the canonical body (see dedupPageBoundaries).
  const [pages, collapsedBoundaryLines] = dedupPageBoundaries(cleaned);
  const combined = concatenatePages(pages);

  // Cover detection (before the split, so an unreadable cover is reported
  // as such rather than as whatever the stanzas make of it). When absent,
  // synthesize from the parsed stanzas (parties by frequency, children
  // empty, assumed timezone — flagged loud).
  const parsedCover = resolveCover(pages[0], template);

  const { stanzas, keptLines, anomalies } = splitStanzas(combined, template);
  if (stanzas.length === 0) {
    throw new MalformedOFWError("no 'Message N of M' stanzas found");
  }
  // A malformed stanza is skipped and warned about, up to the same 10%
  // tolerance the Python adapter applies; past that the file is the wrong
  // format and the whole ingest fails. Lockstep with parse_ofw_pdf.
  const warnings: string[] = [];
  const parsed: ParsedMessage[] = [];
  let malformed = 0;
  for (const s of stanzas) {
    try {
      const pm = parseStanza(s.message_number, s.text, template);
      if (s.conversation_index !== undefined) {
        pm.conversation_index = s.conversation_index;
        pm.index_in_conversation = s.index_in_conversation;
      }
      parsed.push(pm);
    } catch (e) {
      if (!(e instanceof MalformedOFWError)) throw e;
      malformed += 1;
      warnings.push(`malformed message ${s.message_number} skipped: ${e.message}`);
    }
  }
  if (malformed / stanzas.length > 0.1) {
    throw new MalformedOFWError(
      `More than 10% of messages malformed (${malformed}/${stanzas.length}). Likely wrong format.`,
    );
  }

  const coverless = parsedCover === null;
  let cover: CoverInfo;
  if (coverless) {
    const assumedZone = opts.defaultTimezone ?? DEFAULT_ASSUMED_TIMEZONE;
    cover = synthesizeCover(parsed, assumedZone);
    warnings.push(
      `cover-less export — timezone assumed (${assumedZone}); verify/correct`,
    );
  } else {
    cover = parsedCover;
  }

  // Header-sequence guard output (see HeaderSequence): every header-shaped
  // line that was kept as body text, every sequence anomaly, and the
  // cover's own totals against what was parsed. Nothing here changes the
  // rows; all of it is loud so a doctored or damaged export is never read
  // quietly. Lockstep with the WARN lines ofw.py writes to ingest.log.
  for (const k of keptLines) {
    warnings.push(
      `header-shaped line kept as body text in message ${k.message_number} (${k.reason}): ${k.line}`,
    );
  }
  for (const note of anomalies) warnings.push(`header sequence: ${note}`);
  if (!coverless) {
    if (cover.total_messages !== stanzas.length) {
      warnings.push(
        `cover declares ${cover.total_messages} messages; ${stanzas.length} headers accepted`,
      );
    }
    if (template === TEMPLATE_CONVERSATION && cover.conversation_count !== null) {
      const highest = stanzas.reduce(
        (acc, s) => Math.max(acc, s.conversation_index ?? 0),
        0,
      );
      if (cover.conversation_count !== highest) {
        warnings.push(
          `cover declares ${cover.conversation_count} conversations; highest conversation index seen is ${highest}`,
        );
      }
    }
  }

  const { rows, metas, stats: baseStats } = buildRowsAndMetadata(
    parsed,
    cover,
    coverless,
    template,
  );
  // Surface the PDF's page count so the cost-screen tier context line
  // can show a real, user-verifiable page number from the OFW export
  // rather than a token-derived estimate. PDF sources only; other
  // adapters omit this field and downstream falls back to tokens/700.
  const stats: IngestStats = {
    ...baseStats,
    page_count: pages.length,
    header_lines_in_bodies: keptLines.length,
    page_boundary_dups_collapsed: collapsedBoundaryLines.length,
    page_boundary_collapsed_lines: collapsedBoundaryLines.map((ln) => ln.slice(0, 200)),
    malformed_skipped: malformed,
  };
  const csv = rowsToCsv(rows);
  const case_meta = buildCaseMeta(opts.corpusId, stats);

  return {
    rows,
    source_metas: metas,
    stats,
    cover,
    csv,
    case_meta,
    warnings,
  };
}

/**
 * Ingest an OFW Message Report PDF into the canonical CSV shape.
 *
 * Throws MalformedOFWError if the PDF doesn't match the expected layout
 * (no `Message N of M` stanzas, missing per-message labels, etc.). A
 * missing cover page is NOT an error — see ingestOfwFromPages.
 */
export async function ingestOfwPdf(
  pdfData: ArrayBuffer | Uint8Array,
  opts: IngestOptions,
): Promise<IngestResult> {
  const pagesRaw = await extractPdfTextEol(pdfData, opts.onProgress);
  return ingestOfwFromPages(pagesRaw, {
    corpusId: opts.corpusId,
    defaultTimezone: opts.defaultTimezone,
  });
}

// =====================================================================
// OFW CSV adapter (Piece A — spec §9 + §7). Lockstep with
// evals/adapters/ofw.py parse_ofw_csv / is_ofw_csv_headers / score_ofw_csv_headers.
// =====================================================================

import { parseCsvText } from "./generic_csv";

export const OFW_CSV_HEADERS = ["Date", "Time", "Sender", "Message"] as const;

/** True when `headers` is exactly the OFW CSV signature (case-insensitive). */
export function isOfwCsvHeaders(headers: string[]): boolean {
  const norm = headers.map((h) => h.trim().toLowerCase());
  const want = OFW_CSV_HEADERS.map((h) => h.toLowerCase());
  return norm.length === want.length && norm.every((h, i) => h === want[i]);
}

/** Structural confidence that `headers` is an OFW message CSV. Exact signature
 *  → 1.0; the four labels present in any order → 0.7; else 0.0. Lockstep with
 *  ofw.py score_ofw_csv_headers. */
export function scoreOfwCsvHeaders(headers: string[]): number {
  const norm = new Set(headers.map((h) => h.trim().toLowerCase()));
  const want = new Set(OFW_CSV_HEADERS.map((h) => h.toLowerCase()));
  if (norm.size === 0) return 0.0;
  if (isOfwCsvHeaders(headers)) return 1.0;
  const subset = [...want].every((w) => norm.has(w));
  if (subset && norm.size === want.size) return 0.7;
  return 0.0;
}

export interface OfwCsvIngestStats {
  party_a: string;
  party_b: string;
  out_of_set_senders: string[];
  message_count: number;
  accepted_count: number;
  rejected_count: number;
  non_printables_stripped: number;
  date_range: { start: string | null; end: string | null };
  by_reason: Record<string, number>;
  timezone: string | null;
  needs_timezone: boolean;
  input_timezone: string | null;
  zone_source: string | null;
  timezone_assumed: boolean;
}

export interface OfwCsvIngestResult {
  rows: CanonicalRow[];
  stats: OfwCsvIngestStats;
  csv: string;
  case_meta: CaseMeta;
  warnings: string[];
}

export interface OfwCsvIngestOptions {
  corpusId: string;
  /** The user's explicit IANA zone for these naive timestamps (Piece C). */
  inputTimezone?: string;
  /** A best-guess default to ASSUME (and flag) when none was assigned. */
  defaultTimezone?: string;
}

// Parse "MM/DD/YYYY" + "h:mm AM/PM" (naive) → canonical UTC ISO in `zone`.
// Returns null when the Date/Time is unparseable. `zone` null ⇒ no zone to
// place a naive instant (caller treats as missing_timezone).
function parseOfwCsvDateTime(
  dateS: string,
  timeS: string,
  zone: string | null,
): { iso: string | null; parseable: boolean } {
  const combined = `${dateS.trim()} ${timeS.trim()}`.toUpperCase();
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/.exec(
    combined,
  );
  if (!m) return { iso: null, parseable: false };
  let hour = parseInt(m[4], 10);
  const meridian = m[6];
  if (meridian === "PM" && hour !== 12) hour += 12;
  else if (meridian === "AM" && hour === 12) hour = 0;
  if (zone === null) return { iso: null, parseable: true };
  const dt = DateTime.fromObject(
    {
      year: parseInt(m[3], 10),
      month: parseInt(m[1], 10),
      day: parseInt(m[2], 10),
      hour,
      minute: parseInt(m[5], 10),
    },
    { zone: new IANAZone(zone) },
  );
  if (!dt.isValid) return { iso: null, parseable: false };
  return { iso: dt.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'"), parseable: true };
}

/**
 * Ingest an OFW message-export CSV (`Date,Time,Sender,Message`). KNOWN shape →
 * auto-mapped (no generic column-mapping). FULL Message body kept verbatim
 * (embedded quotes included) so Phase-2's quote-split runs downstream. Naive
 * timestamps go through the shared timezone prompt (Piece C); the assumption is
 * flagged exactly like the cover-less PDF path.
 */
export function ingestOfwCsv(
  text: string,
  opts: OfwCsvIngestOptions,
): OfwCsvIngestResult {
  const parsed = parseCsvText(text);
  if (!isOfwCsvHeaders(parsed.header)) {
    throw new MalformedOFWError(
      `Not an OFW message CSV. Expected headers ${OFW_CSV_HEADERS.join(", ")}.`,
    );
  }
  const fieldFor = new Map(parsed.header.map((h) => [h.trim().toLowerCase(), h]));
  const hDate = fieldFor.get("date")!;
  const hTime = fieldFor.get("time")!;
  const hSender = fieldFor.get("sender")!;
  const hMessage = fieldFor.get("message")!;

  let zone: string | null = null;
  let timezoneAssumed = false;
  const warnings: string[] = [];
  if (opts.inputTimezone) {
    zone = opts.inputTimezone;
  } else if (opts.defaultTimezone) {
    zone = opts.defaultTimezone;
    timezoneAssumed = true;
    warnings.push(`timezone assumed (${zone}); verify/correct`);
  }

  const byReason: Record<string, number> = {};
  let nonPrintablesStripped = 0;
  let naiveCount = 0;
  const sendersSeen: string[] = [];
  const accepted: CanonicalRow[] = [];

  for (const r of parsed.rows) {
    const { iso, parseable } = parseOfwCsvDateTime(
      r[hDate] ?? "",
      r[hTime] ?? "",
      zone,
    );
    if (parseable) naiveCount += 1;
    if (iso === null) {
      const reason = parseable ? "missing_timezone" : "invalid_timestamp";
      byReason[reason] = (byReason[reason] ?? 0) + 1;
      continue;
    }
    const [sender, nS] = sanitizeActor(r[hSender] ?? "");
    nonPrintablesStripped += nS;
    if (!sender) {
      byReason.missing_required_field = (byReason.missing_required_field ?? 0) + 1;
      continue;
    }
    if (!sendersSeen.includes(sender)) sendersSeen.push(sender);
    const [body, nB] = sanitizeBody(r[hMessage] ?? "");
    nonPrintablesStripped += nB;
    if (!body) {
      byReason.missing_required_field = (byReason.missing_required_field ?? 0) + 1;
      continue;
    }
    accepted.push({ timestamp: iso, sender, recipient: "", subject: "", body, attachments: "" });
  }

  // Recipient derivation: exactly two distinct senders → the other party.
  if (sendersSeen.length === 2) {
    const [a, b] = sendersSeen;
    for (const row of accepted) {
      row.recipient = row.sender === a ? b : a;
    }
  }
  accepted.sort((x, y) =>
    x.timestamp < y.timestamp ? -1 : x.timestamp > y.timestamp ? 1 : 0,
  );

  const needsTimezone = naiveCount > 0 && zone === null;
  const zoneSource = opts.inputTimezone && zone !== null ? "user_assigned" : null;
  const sourceTimezone = zone !== null ? zone : null;
  const partyA = sendersSeen[0] ?? "";
  const partyB = sendersSeen[1] ?? "";
  const dateRange = {
    start: accepted.length > 0 ? accepted[0].timestamp : null,
    end: accepted.length > 0 ? accepted[accepted.length - 1].timestamp : null,
  };

  const stats: OfwCsvIngestStats = {
    party_a: partyA,
    party_b: partyB,
    out_of_set_senders: sendersSeen.slice(2),
    message_count: accepted.length,
    accepted_count: accepted.length,
    rejected_count: Object.values(byReason).reduce((s, n) => s + n, 0),
    non_printables_stripped: nonPrintablesStripped,
    date_range: dateRange,
    by_reason: byReason,
    timezone: sourceTimezone,
    needs_timezone: needsTimezone,
    input_timezone: sourceTimezone,
    zone_source: zoneSource,
    timezone_assumed: timezoneAssumed,
  };

  // Reuse the OFW case-meta builder (children=[] → generic context); override
  // the format label + the assumed flag.
  const baseStats = {
    party_a: partyA,
    party_b: partyB,
    out_of_set_senders: stats.out_of_set_senders,
    message_count: accepted.length,
    accepted_count: accepted.length,
    rejected_count: stats.rejected_count,
    non_printables_stripped: nonPrintablesStripped,
    rejection_rate_by_party: {},
    date_range: dateRange,
    timezone: sourceTimezone ?? "",
    children: [],
    template: TEMPLATE_PER_MESSAGE,
    conversation_count: null,
    timezone_assumed: timezoneAssumed,
  } as IngestStats;
  const case_meta = buildCaseMeta(opts.corpusId, baseStats);
  case_meta.prompt_vars.format =
    "OurFamilyWizard message CSV (Date/Time/Sender/Message), canonicalized to CSV (schema v1)";
  // A CSV has no report layout; the PDF-only keys must not ride along
  // (Python's parse_ofw_csv writes neither).
  delete case_meta.ingestion.template;
  delete case_meta.ingestion.conversation_count;

  return { rows: accepted, stats, csv: rowsToCsv(accepted), case_meta, warnings };
}
